import {Component, OnInit} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {MatDialog} from '@angular/material/dialog';
import {AdminService} from '../services/admin.service';
import {MatchModalComponent} from './match-modal/match-modal.component';
import Swal from "sweetalert2";
import {TranslateService} from "@ngx-translate/core";
import {EditGameComponent} from "./edit-game/edit-game.component";

@Component({
  selector: 'app-standings',
  templateUrl: './standings.component.html',
  styleUrls: ['./standings.component.scss'],
})
export class StandingsComponent implements OnInit {
  leagues: any[] = [];
  standings = new MatTableDataSource<any>();
  weeks: any[] = [];
  weekMatches: { [key: number]: any[] } = {};
  selectedLeagueId: string | null = null;

  displayedStandingsColumns: string[] = [
    'team',
    'matchesPlayed',
    'wins',
    'draws',
    'losses',
    'goalsScored',
    'goalsAgainst',
    'goalDifference',
    'points',
  ];
  displayedMatchColumns: string[] = ['homeTeam', 'score', 'awayTeam', 'date', 'status', 'edit', 'delete'];

  constructor(
    private adminService: AdminService,
    public dialog: MatDialog,
    private translate: TranslateService
  ) {
  }

  ngOnInit(): void {
    this.loadLeagues();
  }

  loadLeagues(): void {
    this.adminService.getAllLeagues(0, 20).subscribe((data) => {
      this.leagues = data.content;
    });
  }

  onLeagueSelectionChange(): void {
    if (this.selectedLeagueId) {
      this.loadStandings(this.selectedLeagueId);
      this.loadWeeksPerLeague(this.selectedLeagueId);
    } else {
      this.standings.data = [];
      this.weeks = [];
      this.weekMatches = {};
    }
  }

  loadStandings(leagueId: string): void {
    this.adminService.getStandingsForALeague(leagueId).subscribe(
      (data: any) => {
        this.standings.data = data;
      },
      (error: any) => {
        console.error('Error loading standings:', error);
      }
    );
  }

  loadWeeksPerLeague(leagueId: string): void {
    this.adminService.getWeeksPerLeague(leagueId).subscribe(
      (data: any) => {
        this.weeks = data.map((weekNumber: number) => ({weekNumber}));
        this.weeks.forEach((week) => {
          this.loadWeekMatches(leagueId, week.weekNumber);
        });
      },
      (error: any) => {
        console.error('Error loading weeks per league:', error);
      }
    );
  }

  loadWeekMatches(leagueId: string, weekNumber: number): void {
    this.adminService.getWeekMatches(leagueId, weekNumber).subscribe(
      (matches) => {
        this.weekMatches[weekNumber] = matches.map((match: any) => ({
          ...match,
          score: `${match.homeTeamScore} - ${match.awayTeamScore}`,
          date: new Date(match.date)
        }));
      },
      (error: any) => {
        console.error(`Error loading matches for week ${weekNumber}:`, error);
      }
    );
  }

  openCreateMatchesModal(): void {
    if (this.selectedLeagueId) {
      const dialogRef = this.dialog.open(MatchModalComponent, {
        width: '800px',
        data: {leagueId: this.selectedLeagueId},
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadStandings(this.selectedLeagueId!);
          this.loadWeeksPerLeague(this.selectedLeagueId!);
        }
      });
    } else {
      alert('Please select a league to create matches!');
    }
  }

  openEditMatchModal(matchId: string): void {
    this.adminService.getMatches(matchId).subscribe((matchDetails) => {
      const dialogRef = this.dialog.open(EditGameComponent, {
        width: '600px',
        data: { match: matchDetails }
      });

      dialogRef.afterClosed().subscribe((updated) => {
        if (updated) {
          this.loadStandings(this.selectedLeagueId!);
          this.loadWeeksPerLeague(this.selectedLeagueId!);
        }
      });
    }, (error) => {
      console.error('Error fetching match details:', error);
      alert('Failed to load match details. Please try again.');
    });
  }

  deleteMatch(matchId: string): void {
    this.translate.get([
      'Are you sure?',
      'You won\'t be able to revert this!',
      'Yes, delete it!',
      'No, cancel!',
      'Deleted',
      'The match has been deleted.',
      'Error',
      'An error occurred while deleting the match.'
    ]).subscribe(translations => {
      Swal.fire({
        title: translations['Are you sure?'],
        text: translations['You won\'t be able to revert this!'],
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: translations['Yes, delete it!'],
        cancelButtonText: translations['No, cancel!']
      }).then((result: any) => {
        if (result.isConfirmed) {
          this.adminService.deleteWeekMatches(matchId).subscribe(() => {
            Swal.fire(translations['Deleted'], translations['The match has been deleted.'], 'success');
            this.loadLeagues();
            this.loadStandings(this.selectedLeagueId!);
            this.loadWeeksPerLeague(this.selectedLeagueId!)
          }, (error) => {
            Swal.fire(translations['Error'], error.error.message, 'error');
          });
        }
      });
    });
  }

}
